import { ConfigurationValues, JSONRecordData } from '../Log/graph.util';
import {
  Alert,
  Box,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Modal,
  Select,
  TextField,
  Typography,
  useTheme
} from '@mui/material';

import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import MenuItem from '@mui/material/MenuItem';
import axiosConfig from '../../../Resources/axiosConfig';
import { UserContext } from '../../../contexts/userContext';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

interface SodistoreHomeConfigurationProps {
  values: JSONRecordData;
  id: number;
  installation: I_Installation;
}

function SodistoreHomeConfiguration(props: SodistoreHomeConfigurationProps) {
  if (props.values === null) {
    return null;
  }

  const device = props.installation.device;

  const OperatingPriorityOptions =
    device === 3 // Growatt
      ? ['LoadPriority', 'BatteryPriority', 'GridPriority']
      : device === 4 // Sinexcel
        ? [
          'SpontaneousSelfUse',
          'TimeChargeDischarge',
          // 'TimeOfUsePowerPrice',
          // 'DisasterStandby',
          // 'ManualControl',
          'PvPriorityCharging',
          // 'PrioritySellElectricity'
        ]
        : [];

  const [errors, setErrors] = useState({
    minimumSoC: false,
    gridSetPoint: false
  });

  const SetErrorForField = (field_name, state) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field_name]: state
    }));
  };
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [dateSelectionError, setDateSelectionError] = useState('');
  const [isErrorDateModalOpen, setErrorDateModalOpen] = useState(false);
  const context = useContext(UserContext);
  const { currentUser, setUser } = context;
  const { product, setProduct } = useContext(ProductIdContext);

  const [formValues, setFormValues] = useState<Partial<ConfigurationValues>>({
    minimumSoC: props.values.Config.MinSoc,
    maximumDischargingCurrent: props.values.Config.MaximumChargingCurrent,
    maximumChargingCurrent: props.values.Config.MaximumDischargingCurrent,
    operatingPriority: OperatingPriorityOptions.indexOf(
      props.values.Config.OperatingPriority
    ),
    batteriesCount: props.values.Config.BatteriesCount,
    clusterNumber: props.values.Config.ClusterNumber??1,
    PvNumber: props.values.Config.PvNumber??0,
    timeChargeandDischargePower: props.values.Config?.TimeChargeandDischargePower ?? 0, // default 0 W
    startTimeChargeandDischargeDayandTime:
      props.values.Config?.StartTimeChargeandDischargeDayandTime
        ? dayjs(props.values.Config.StartTimeChargeandDischargeDayandTime).toDate()
        : null,
    stopTimeChargeandDischargeDayandTime:
      props.values.Config?.StopTimeChargeandDischargeDayandTime
        ? dayjs(props.values.Config.StopTimeChargeandDischargeDayandTime).toDate()
        : null,

    // controlPermission: props.values.Config.ControlPermission??false,
    controlPermission: String(props.values.Config.ControlPermission).toLowerCase() === "true",
  });

  const handleOperatingPriorityChange = (event) => {
    setFormValues({
      ...formValues,
      ['operatingPriority']: OperatingPriorityOptions.indexOf(
        event.target.value
      )
    });
  };

// Add time validation function
  const validateTimeOnly = () => {
    if (formValues.startTimeChargeandDischargeDayandTime &&
      formValues.stopTimeChargeandDischargeDayandTime) {
      const startHours = formValues.startTimeChargeandDischargeDayandTime.getHours();
      const startMinutes = formValues.startTimeChargeandDischargeDayandTime.getMinutes();
      const stopHours = formValues.stopTimeChargeandDischargeDayandTime.getHours();
      const stopMinutes = formValues.stopTimeChargeandDischargeDayandTime.getMinutes();

      const startTimeInMinutes = startHours * 60 + startMinutes;
      const stopTimeInMinutes = stopHours * 60 + stopMinutes;

      if (startTimeInMinutes >= stopTimeInMinutes) {
        setDateSelectionError('Stop time must be later than start time');
        setErrorDateModalOpen(true);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    if (!validateTimeOnly()) {
      return;
    }
    const configurationToSend: Partial<ConfigurationValues> = {
      minimumSoC: formValues.minimumSoC,
      maximumDischargingCurrent: formValues.maximumDischargingCurrent,
      maximumChargingCurrent: formValues.maximumChargingCurrent,
      operatingPriority: formValues.operatingPriority,
      batteriesCount:formValues.batteriesCount,
      clusterNumber:formValues.clusterNumber,
      PvNumber:formValues.PvNumber,
      timeChargeandDischargePower: formValues.timeChargeandDischargePower,
      startTimeChargeandDischargeDayandTime: formValues.startTimeChargeandDischargeDayandTime
        ? new Date(formValues.startTimeChargeandDischargeDayandTime.getTime() - formValues.startTimeChargeandDischargeDayandTime.getTimezoneOffset() * 60000)
        : null,
      stopTimeChargeandDischargeDayandTime: formValues.stopTimeChargeandDischargeDayandTime
        ? new Date(formValues.stopTimeChargeandDischargeDayandTime.getTime() - formValues.stopTimeChargeandDischargeDayandTime.getTimezoneOffset() * 60000)
        : null,
      controlPermission:formValues.controlPermission
    };

    setLoading(true);
    const res = await axiosConfig
      .post(
        `/EditInstallationConfig?installationId=${props.id}&product=${product}`,
        configurationToSend
      )
      .catch((err) => {
        if (err.response) {
          setError(true);
          setLoading(false);
        }
      });

    if (res) {
      setUpdated(true);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'minimumSoC') {
      const numValue = parseFloat(value);

      // invalid characters or not a number
      if (/[^0-9.]/.test(value) || isNaN(numValue)) {
        SetErrorForField(name, 'Invalid number format');
      } else {
        const minsocRanges = {
          3: { min: 10, max: 30 },
          4: { min: 5, max: 100 },
        };

        const { min, max } = minsocRanges[device] || { min: 10, max: 30 };

        if (numValue < min || numValue > max) {
          SetErrorForField(name, `Value should be between ${min}-${max}%`);
        } else {
          // ✅ valid → clear error
          SetErrorForField(name, '');
        }
      }
    }

    setFormValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTimeChargeDischargeChange = (name: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOkOnErrorDateModal = () => {
    setErrorDateModalOpen(false);
  };

  return (
    <Container maxWidth="xl">
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        {isErrorDateModalOpen && (
          <Modal open={isErrorDateModalOpen} onClose={() => {}}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 450,
                bgcolor: 'background.paper',
                borderRadius: 4,
                boxShadow: 24,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                {dateSelectionError}
              </Typography>

              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={handleOkOnErrorDateModal}
              >
                Ok
              </Button>
            </Box>
          </Modal>
        )}

        <Grid item xs={12} md={12}>
          <CardContent>
            <Box
              component="form"
              sx={{
                '& .MuiTextField-root': { m: 1, width: 390 }
              }}
              noValidate
              autoComplete="off"
            >
              <>
                {device === 3 && (
                  <div style={{ marginBottom: '5px' }}>
                    <FormControlLabel
                      labelPlacement="start"
                      control={
                        <Switch
                          name="controlPermission"
                          checked={Boolean(formValues.controlPermission)}
                          onChange={(e) =>
                            setFormValues((prev) => ({
                              ...prev,
                              controlPermission: e.target.checked,
                            }))
                          }
                          sx={{ transform: "scale(1.4)", marginLeft: "15px" }}
                        />
                      }

                      label={
                        <FormattedMessage
                          id="controlPermission"
                          defaultMessage="Control Permission"
                        />
                      }
                    />
                  </div>
                )}

                <div style={{ marginBottom: '5px' }}>
                  <TextField
                    label={
                      <FormattedMessage
                        id="batteriesCount "
                        defaultMessage="Batteries Count"
                      />
                    }
                    name="batteriesCount"
                    value={formValues.batteriesCount}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                {device === 4 && (
                  <>
                    <div style={{ marginBottom: '5px' }}>
                      <TextField
                        label={
                          <FormattedMessage
                            id="clusterNumber"
                            defaultMessage="Cluster Number"
                          />
                        }
                        name="clusterNumber"
                        value={formValues.clusterNumber}
                        onChange={handleChange}
                        fullWidth
                      />
                    </div>

                    <div style={{ marginBottom: '5px' }}>
                      <TextField
                        label={
                          <FormattedMessage
                            id="PvNumber"
                            defaultMessage="PV Number"
                          />
                        }
                        name="PvNumber"
                        value={formValues.PvNumber}
                        onChange={handleChange}
                        fullWidth
                      />
                    </div>
                  </>
                )}


                <div style={{ marginBottom: '5px' }}>
                  {/*<TextField*/}
                  {/*  label={*/}
                  {/*    <FormattedMessage*/}
                  {/*      id="minimum_soc "*/}
                  {/*      defaultMessage="Minimum SoC (%)"*/}
                  {/*    />*/}
                  {/*  }*/}
                  {/*  name="minimumSoC"*/}
                  {/*  value={formValues.minimumSoC}*/}
                  {/*  onChange={handleChange}*/}
                  {/*  helperText={*/}
                  {/*    errors.minimumSoC ? (*/}
                  {/*      <span style={{ color: 'red' }}>*/}
                  {/*        Value should be between {device === 4 ? '5–100' : '10–30'}%*/}
                  {/*      </span>*/}
                  {/*    ) : (*/}
                  {/*      ''*/}
                  {/*    )*/}
                  {/*  }*/}
                  {/*  fullWidth*/}
                  {/*/>*/}
                  <TextField
                    label="Minimum SoC (%)"
                    name="minimumSoC"
                    value={formValues.minimumSoC}
                    onChange={handleChange}
                    error={Boolean(errors.minimumSoC)}
                    helperText={errors.minimumSoC}
                    fullWidth
                  />

                </div>

                <div style={{ marginBottom: '5px' }}>
                  <TextField
                    label={
                      <FormattedMessage
                        id="maximumChargingCurrent "
                        defaultMessage="Maximum Charging Current"
                      />
                    }
                    name="maximumChargingCurrent"
                    value={formValues.maximumChargingCurrent}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                <div style={{ marginBottom: '5px' }}>
                  <TextField
                    label={
                      <FormattedMessage
                        id="maximumDischargingCurrent "
                        defaultMessage="Maximum Discharging Current"
                      />
                    }
                    name="maximumDischargingCurrent"
                    value={formValues.maximumDischargingCurrent}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                <div style={{ marginBottom: '5px', marginTop: '10px' }}>
                  <FormControl fullWidth sx={{ marginLeft: 1, width: 390 }}>
                    <InputLabel
                      sx={{
                        fontSize: 14,
                        backgroundColor: 'white'
                      }}
                    >
                      <FormattedMessage
                        id="operating_priority"
                        defaultMessage="Operating Priority"
                      />
                    </InputLabel>
                    <Select
                      value={
                        OperatingPriorityOptions[formValues.operatingPriority]
                      }
                      onChange={handleOperatingPriorityChange}
                    >
                      {OperatingPriorityOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </>

              {/* --- Sinexcel + TimeChargeDischarge --- */}
              {device === 4 &&
                OperatingPriorityOptions[formValues.operatingPriority] ===
                'TimeChargeDischarge' && (
                  <>
                    {/* Power input*/}
                    <div style={{ marginBottom: '5px' }}>
                      <TextField
                        label="Power (W)"
                        name="timeChargeandDischargePower"
                        value={formValues.timeChargeandDischargePower}
                        onChange={(e) =>
                          handleTimeChargeDischargeChange(e.target.name, e.target.value)
                        }
                        helperText="Enter a positive or negative power value"
                        fullWidth
                      />
                    </div>

                    {/* Start DateTime */}
                    <div style={{ marginBottom: '5px' }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          ampm={false}
                          label="Start Date and Time (Start Time < Stop Time)"
                          value={
                            formValues.startTimeChargeandDischargeDayandTime
                              ? dayjs(formValues.startTimeChargeandDischargeDayandTime)
                              : null
                          }
                          onChange={(newValue) =>
                            setFormValues((prev) => ({
                              ...prev,
                              startTimeChargeandDischargeDayandTime: newValue
                                ? newValue.toDate()
                                : null,
                            }))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{
                                marginTop: 2,
                                width: '100%',
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>

                    {/* Stop DateTime */}
                    <div style={{ marginBottom: '5px' }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          ampm={false}
                          label="Stop Date and Time (Start Time < Stop Time)"
                          value={
                            formValues.stopTimeChargeandDischargeDayandTime
                              ? dayjs(formValues.stopTimeChargeandDischargeDayandTime)
                              : null
                          }
                          onChange={(newValue) =>
                            setFormValues((prev) => ({
                              ...prev,
                              stopTimeChargeandDischargeDayandTime: newValue
                                ? newValue.toDate()
                                : null,
                            }))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{
                                marginTop: 2,
                                width: '100%',
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </>
                )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 10
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    marginLeft: '10px'
                  }}
                >
                  <FormattedMessage
                    id="applychanges"
                    defaultMessage="Apply Changes"
                  />
                </Button>
                {loading && (
                  <CircularProgress
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: '20px'
                    }}
                  />
                )}

                {updated && (
                  <Alert
                    severity="success"
                    sx={{
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    Successfully applied configuration file
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setUpdated(false)}
                      sx={{ marginLeft: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Alert>
                )}
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    An error has occurred
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setError(false)}
                      sx={{ marginLeft: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Alert>
                )}
              </div>
            </Box>
          </CardContent>
        </Grid>
      </Grid>
    </Container>
  );
}

export default SodistoreHomeConfiguration;
